import React from 'react';
import { ChartTooltip } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

/**
 * Shape of the props Recharts injects when this component is used as a
 * `<Tooltip content>` element. Under recharts' `layout="vertical"`
 * (which renders horizontal bars), the category name sits on
 * `payload[i].payload.categoryName` — the YAxis data key — and the
 * revenue sits on `payload[i].value` — the Bar's data key.
 */
export type CategoryAnalysisTooltipProps = {
    active?: boolean;
    payload?: ReadonlyArray<{
        color?: string;
        value?: number | string;
        payload?: { categoryName?: string; fill?: string };
    }>;
    valueFormatter?: (value: number | string) => React.ReactNode;
};

/**
 * Bar tooltip for the category chart. Labels the hovered bar with the
 * server-supplied `categoryName` and shows its currency-formatted
 * revenue. Returns `null` when inactive so recharts can keep the
 * tooltip layer hidden.
 */
export const CategoryAnalysisTooltip: React.FC<
    CategoryAnalysisTooltipProps
> = ({ active, payload, valueFormatter }) => {
    if (!active || !payload?.length) {
        return null;
    }

    return (
        <ChartTooltip {...getNodeTestIdProp('category-analysis-tooltip')}>
            {payload.map((entry, index) => {
                const label = entry.payload?.categoryName ?? '';
                const indicatorColor = entry.color ?? entry.payload?.fill;

                return (
                    <ChartTooltip.Item
                        key={`${label}-${index}`}
                        {...getNodeTestIdProp(
                            `category-analysis-tooltip-item-${index}`,
                        )}
                    >
                        <ChartTooltip.Indicator
                            color={indicatorColor}
                            {...getNodeTestIdProp(
                                `category-analysis-tooltip-indicator-${index}`,
                            )}
                        />
                        <ChartTooltip.Label
                            {...getNodeTestIdProp(
                                `category-analysis-tooltip-label-${index}`,
                            )}
                        >
                            {label}
                        </ChartTooltip.Label>
                        <ChartTooltip.Value
                            {...getNodeTestIdProp(
                                `category-analysis-tooltip-value-${index}`,
                            )}
                        >
                            {valueFormatter
                                ? valueFormatter(entry.value ?? '')
                                : entry.value}
                        </ChartTooltip.Value>
                    </ChartTooltip.Item>
                );
            })}
        </ChartTooltip>
    );
};
